import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Cookie, Database, Mail, Globe, Users } from 'lucide-react';
import LightBackground from '@/components/LightBackground';

const PrivacyPolicy = () => {
  const lastUpdated = 'May 13, 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy – Faceless Aura Club</title>
        <meta 
          name="description" 
          content="Privacy Policy for Faceless Aura Club. Learn how we collect, use, and protect your personal data." 
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative">
        <LightBackground />
        
        {/* Header */}
        <header className="relative z-10 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="relative z-10 max-w-4xl mx-auto px-6 py-12 md:py-16">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Policy Content */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-10 space-y-10">
            
            {/* Introduction */}
            <section>
              <p className="text-slate-700 leading-relaxed">
                Faceless Aura Club ("we," "our," or "us") respects your privacy and is committed to protecting 
                your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you visit our website <strong>facelessaura.club</strong> and use our services.
              </p>
            </section>

            {/* Data Collection */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">1. Information We Collect</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p><strong>Personal Information:</strong> When you create an account, apply for membership, or make a purchase, we collect:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Name and email address</li>
                  <li>Billing information (processed securely via Stripe)</li>
                  <li>Country of residence</li>
                  <li>Application details (motivation letter, if applicable)</li>
                </ul>
                <p><strong>Automatically Collected Information:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our site</li>
                  <li>Referring website or source</li>
                </ul>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Cookie className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">2. Cookies & Tracking Technologies</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>We use cookies and similar tracking technologies to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Remember your preferences and settings</li>
                  <li>Analyze site traffic and usage patterns</li>
                  <li>Deliver personalized advertising through Meta (Facebook) Pixel</li>
                  <li>Measure the effectiveness of our marketing campaigns</li>
                </ul>
                <p>
                  <strong>Your Choices:</strong> When you first visit our site, you'll see a cookie consent banner. 
                  You can accept or decline non-essential cookies. If you decline, we will not track your activity 
                  for advertising purposes. You can also manage cookies through your browser settings.
                </p>
                <p><strong>Third-Party Cookies:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Meta Pixel:</strong> Used for conversion tracking and retargeting ads</li>
                  <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
                </ul>
              </div>
            </section>

            {/* How We Use Data */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">3. How We Use Your Information</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>We use your personal information to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Provide and maintain our services</li>
                  <li>Process your membership and payments</li>
                  <li>Send you important updates about your account</li>
                  <li>Respond to your inquiries and support requests</li>
                  <li>Improve our website and user experience</li>
                  <li>Send marketing communications (with your consent)</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-slate-900">4. Data Sharing & Third Parties</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>We do not sell your personal data. We may share your information with:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Service Providers:</strong> Stripe (payments), Supabase (database hosting), Resend (email delivery)</li>
                  <li><strong>Advertising Partners:</strong> Meta/Facebook (only if you consent to cookies)</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                </ul>
              </div>
            </section>

            {/* GDPR Rights */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">5. Your Rights (GDPR & CCPA)</h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                  <li><strong>Restriction:</strong> Limit how we process your data</li>
                  <li><strong>Portability:</strong> Receive your data in a structured format</li>
                  <li><strong>Objection:</strong> Opt out of certain processing activities</li>
                  <li><strong>Withdraw Consent:</strong> Revoke consent at any time</li>
                </ul>
                <p>
                  To exercise these rights, contact us at{' '}
                  <a href="mailto:hello@auradynamics.management" className="text-blue-600 hover:text-blue-700 underline">
                    hello@auradynamics.management
                  </a>
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Data Security</h2>
              <p className="text-slate-700 leading-relaxed">
                We implement industry-standard security measures to protect your data, including encryption 
                (SSL/TLS), secure payment processing via Stripe, and access controls. However, no method of 
                transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Data Retention</h2>
              <p className="text-slate-700 leading-relaxed">
                We retain your personal data for as long as your account is active or as needed to provide 
                services. We may retain certain information for legal, accounting, or business purposes for 
                up to 7 years after account closure.
              </p>
            </section>

            {/* Children */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Children's Privacy</h2>
              <p className="text-slate-700 leading-relaxed">
                Our services are not intended for individuals under 18 years of age. We do not knowingly 
                collect personal information from children. If you believe we have collected data from a 
                minor, please contact us immediately.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-slate-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new policy on this page and updating the "Last updated" date. 
                Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-slate-900">10. Contact Us</h2>
              </div>
              <div className="text-slate-700 leading-relaxed space-y-2">
                <p>If you have questions about this Privacy Policy or your personal data, contact us at:</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:hello@auradynamics.management" className="text-blue-600 hover:text-blue-700 underline">
                    hello@auradynamics.management
                  </a>
                </p>
                <p><strong>Website:</strong> facelessaura.club</p>
              </div>
            </section>

          </div>

          {/* Footer link */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
            >
              Return to Homepage
            </Link>
          </div>
        </main>
      </div>
    </>
  );
};

export default PrivacyPolicy;
